import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Text, useTheme } from '@Sage/hoi-poi-ui';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import MobileEmailPreview from '../MobileEmailPreview';
import TextareaWithFieldInsert from '../TextareaWithFieldInsert';
import { formatCampaignReplaceInsert } from '../formatCampaignReplaceInsert';

import './styles.scss';

const SUBJECT_AND_PREVIEW_TEXT_MAX_LENGTH = 255;

const SubjectForm = memo(
    ({
        subject,
        previewText,
        senderDisplayName,
        onSubjectChange,
        onPreviewTextChange,
        onCancel,
        onSave,
        isSaveLoading = false,
    }) => {
        const theme = useTheme();
        const [showLengthErrors, setShowLengthErrors] = useState(false);
        const subjectInputRef = useRef(null);
        const previewTextInputRef = useRef(null);

        const handleSubjectChange = useCallback(
            (value) => {
                onSubjectChange?.(value);
            },
            [onSubjectChange],
        );

        const handlePreviewTextChange = useCallback(
            (value) => {
                onPreviewTextChange?.(value);
            },
            [onPreviewTextChange],
        );

        const handleCancel = useCallback(() => {
            setShowLengthErrors(false);
            onCancel?.();
        }, [onCancel]);

        const subjectValue = useMemo(() => subject || '', [subject]);
        const previewTextValue = useMemo(() => previewText || '', [previewText]);
        const subjectLengthError = useMemo(() => {
            if (subjectValue.length <= SUBJECT_AND_PREVIEW_TEXT_MAX_LENGTH) return '';
            return getLiteralWithParameters('error_text_too_long', [
                SUBJECT_AND_PREVIEW_TEXT_MAX_LENGTH,
            ]);
        }, [subjectValue]);
        const previewTextLengthError = useMemo(() => {
            if (previewTextValue.length <= SUBJECT_AND_PREVIEW_TEXT_MAX_LENGTH) return '';
            return getLiteralWithParameters('error_text_too_long', [
                SUBJECT_AND_PREVIEW_TEXT_MAX_LENGTH,
            ]);
        }, [previewTextValue]);

        const handleSave = useCallback(() => {
            setShowLengthErrors(true);
            if (subjectLengthError || previewTextLengthError) {
                return;
            }
            setShowLengthErrors(false);
            onSave?.({
                subject: subjectInputRef.current?.getValue?.() ?? subjectValue,
                previewText: previewTextInputRef.current?.getValue?.() ?? previewTextValue,
            });
        }, [onSave, previewTextLengthError, previewTextValue, subjectLengthError, subjectValue]);

        const isValid = useMemo(() => subjectValue.trim().length > 0, [subjectValue]);

        return (
            <div className="subject-form">
                <div className="subject-form__content">
                    <div className="subject-form__fields">
                        <TextareaWithFieldInsert
                            inputRef={subjectInputRef}
                            value={subjectValue}
                            onChange={handleSubjectChange}
                            label={getLiteral('label_subject')}
                            hint={getLiteral('label_marketing_campaigns_subject_info')}
                            isRequired
                            minRows={3}
                            useCampaignReplaceVariables
                            formatInsertedVariable={formatCampaignReplaceInsert}
                            error={showLengthErrors && subjectLengthError}
                        />
                        <TextareaWithFieldInsert
                            inputRef={previewTextInputRef}
                            value={previewTextValue}
                            onChange={handlePreviewTextChange}
                            label={getLiteral('label_preview_text')}
                            hint={getLiteral('label_marketing_campaigns_preview_text_info')}
                            minRows={3}
                            useCampaignReplaceVariables
                            formatInsertedVariable={formatCampaignReplaceInsert}
                            error={showLengthErrors && previewTextLengthError}
                        />
                    </div>

                    <div className="subject-form__preview">
                        <MobileEmailPreview
                            name={senderDisplayName}
                            message={subjectValue || 'Message subject...'}
                            previewText={previewTextValue || getLiteral('label_preview_text')}
                            variant="large"
                        />
                        <Text
                            type="caption"
                            color={theme.colors.textLight.secondary}
                            className="subject-form__preview-note"
                        >
                            {getLiteral('label_marketing_campaigns_email_preview_may_vary')}
                        </Text>
                    </div>
                </div>

                <div className="subject-form__actions">
                    <Button type="secondary" size="small" onClick={handleCancel}>
                        {getLiteral('action_cancel')}
                    </Button>
                    <Button
                        type="primary"
                        size="small"
                        onClick={handleSave}
                        isDisabled={!isValid || isSaveLoading}
                        isLoading={isSaveLoading}
                    >
                        {getLiteral('action_save')}
                    </Button>
                </div>
            </div>
        );
    },
);

SubjectForm.displayName = 'SubjectForm';

SubjectForm.propTypes = {
    subject: PropTypes.string,
    previewText: PropTypes.string,
    senderDisplayName: PropTypes.string,
    onSubjectChange: PropTypes.func,
    onPreviewTextChange: PropTypes.func,
    onCancel: PropTypes.func,
    onSave: PropTypes.func,
    isSaveLoading: PropTypes.bool,
};

export default SubjectForm;
